if (!customElements.get('brand-slider')) {
  class BrandSlider extends HTMLElement {
    constructor() {
      super();
      
      this.container = this.querySelector('.brand-slider__container');
      this.track = this.querySelector('.brand-slider__track');
      this.slides = this.querySelectorAll('.brand-slider__slide');
      this.pauseButton = this.querySelector('.brand-slider__control-button--pause');
      
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed) || 40;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.isPaused = false;
      
      this.init();
    }

    init() {
      if (!this.container || !this.track || this.slides.length === 0) return;
      
      // Clone slides for seamless infinite scroll
      this.cloneSlides();
      
      // Set animation speed
      this.setSpeed();
      
      // Setup controls
      this.setupControls();
      
      // Setup hover pause
      if (this.pauseOnHover) {
        this.setupHoverPause();
      }
      
      // Handle reduced motion preference
      this.handleReducedMotion();
      
      // Adjust animation on window resize
      this.handleResize();
    }

    cloneSlides() {
      const slidesArray = Array.from(this.slides);
      
      // Clone all slides and append to create seamless loop
      slidesArray.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('brand-slider__slide--clone');
        this.track.appendChild(clone);
      });
    }

    setSpeed() {
      this.style.setProperty('--brand-slider-speed', `${this.speed}s`);
    }

    setupControls() {
      if (!this.pauseButton) return;
      
      this.pauseButton.addEventListener('click', () => {
        this.togglePause();
      });
    }

    togglePause() {
      this.isPaused = !this.isPaused;
      
      if (this.isPaused) {
        this.pause();
      } else {
        this.play();
      }
    }

    pause() {
      this.classList.add('brand-slider--paused');
      this.isPaused = true;
      
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-label', 'Play slider');
      }
    }

    play() {
      this.classList.remove('brand-slider--paused');
      this.isPaused = false;
      
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-label', 'Pause slider');
      }
    }

    setupHoverPause() {
      this.style.setProperty('--brand-slider-hover-state', 'paused');
    }

    handleReducedMotion() {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      
      const handleChange = (e) => {
        if (e.matches) {
          this.pause();
        } else if (this.autoplay && !this.isPaused) {
          this.play();
        }
      };
      
      // Initial check
      if (mediaQuery.matches) {
        this.pause();
      }
      
      // Listen for changes
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.addListener(handleChange);
      }
    }

    handleResize() {
      let resizeTimer;
      
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.adjustAnimation();
        }, 250);
      });
    }

    adjustAnimation() {
      // Recalculate animation if needed based on new dimensions
      // This ensures smooth animation after window resize
      const wasPlaying = !this.isPaused;
      
      if (wasPlaying) {
        this.pause();
        requestAnimationFrame(() => {
          this.play();
        });
      }
    }

    // Public methods for external control
    stop() {
      this.pause();
    }

    start() {
      if (this.autoplay) {
        this.play();
      }
    }

    setAutoplaySpeed(speed) {
      this.speed = speed;
      this.setSpeed();
    }
  }

  customElements.define('brand-slider', BrandSlider);
}

// Theme editor support
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const brandSlider = event.target.querySelector('brand-slider');
    if (brandSlider) {
      // Reinitialize the slider
      brandSlider.init();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const brandSlider = event.target.querySelector('brand-slider');
    if (brandSlider) {
      brandSlider.pause();
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const brandSlider = event.target.querySelector('brand-slider');
    if (brandSlider && brandSlider.autoplay) {
      brandSlider.play();
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const brandSlider = event.target.closest('brand-slider');
    if (brandSlider) {
      brandSlider.pause();
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const brandSlider = event.target.closest('brand-slider');
    if (brandSlider && brandSlider.autoplay) {
      brandSlider.play();
    }
  });
}
